import { useMemo } from 'react'
import { Upload, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface ImageGalleryInputProps {
  /** Provided by Ant Form.Item — ordered list of image URLs. */
  value?: string[]
  onChange?: (value: string[]) => void
}

/**
 * Multi-image uploader that stores an ordered array of URLs. Uploads go
 * through the API's proxied /storage/upload endpoint (folder: products),
 * which returns a public URL we persist on the product.
 */
export function ImageGalleryInput({ value = [], onChange }: ImageGalleryInputProps) {
  const fileList = useMemo<UploadFile[]>(
    () =>
      value.map((url, i) => ({
        uid: `${i}-${url}`,
        name: url.split('/').pop() || `slika-${i + 1}`,
        status: 'done',
        url,
      })),
    [value],
  )

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    try {
      const token = localStorage.getItem('admin_access_token')
      const formData = new FormData()
      formData.append('file', file as Blob)
      formData.append('folder', 'products')

      const res = await fetch(`${API_URL}/storage/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      })
      if (!res.ok) throw new Error(`Upload failed (${res.status})`)
      const data = await res.json()
      onChange?.([...value, data.url])
      onSuccess?.(data)
    } catch (err) {
      message.error('Otpremanje slike nije uspelo')
      onError?.(err as Error)
    }
  }

  const handleRemove = (file: UploadFile) => {
    onChange?.(value.filter((url) => url !== file.url))
  }

  return (
    <Upload
      listType="picture-card"
      fileList={fileList}
      customRequest={customRequest}
      onRemove={handleRemove}
      accept="image/png,image/jpeg,image/webp"
      multiple
    >
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>Dodaj sliku</div>
      </div>
    </Upload>
  )
}
